/**
 * Orbis Sitz-Limits — TS-Spiegel von `10_Server_Config/orbis_seat_limits.js`
 * Kanon Produkt: RECHTSTEXTE_KONSISTENZ.md (Professional 5 / Premium 10 / max 20;
 * Demo 25 nur E-Mail-Allowlist fail-closed — 14.08.2026).
 */

export const INCLUDED_PROFESSIONAL = 5;
export const INCLUDED_PREMIUM = 10;
export const CUSTOMER_MAX_SEATS = 20;
export const INTERNAL_DEMO_MAX_SEATS = 25;

/** Nur diese zwei Accounts dürfen 25 Sitze — nie status-only. */
export const INTERNAL_DEMO_EMAIL_ALLOWLIST = [
  'orbis-demo@arkorbit.at',
  'thomas@arkorbit.at',
] as const;

export type OrbisSeatOpts = {
  tier?: string | null;
  includedSeats?: number | null;
  maxSeats?: number | null;
  extraSeats?: number | null;
  email?: string | null;
  status?: string | null;
  accountKind?: string | null;
  /** @deprecated Ignoriert — Demo-25 nur über E-Mail-Allowlist. */
  allowInternalDemo?: boolean;
  enterpriseUnlimited?: boolean;
};

function normalizeTier(tier?: string | null): string {
  return String(tier || '')
    .trim()
    .toLowerCase();
}

function normalizeEmail(email?: string | null): string {
  return String(email || '')
    .trim()
    .toLowerCase();
}

/** Professional (standard) = 5, Premium = 10. */
export function includedSeatsForTier(tier?: string | null): number {
  const k = normalizeTier(tier);
  if (k === 'premium') return INCLUDED_PREMIUM;
  if (k === 'enterprise' || k === 'c') return INCLUDED_PREMIUM;
  return INCLUDED_PROFESSIONAL;
}

/** 25 Sitze nur bei E-Mail auf der Allowlist — nie für normale Kunden / status=FIRMA_DEMO allein. */
export function isInternalDemoSeatAllowance(opts: OrbisSeatOpts = {}): boolean {
  const email = normalizeEmail(opts.email);
  if (!email) return false;
  return (INTERNAL_DEMO_EMAIL_ALLOWLIST as readonly string[]).includes(email);
}

export function resolveMaxSeats(opts: OrbisSeatOpts = {}): number {
  let included = Number(opts.includedSeats);
  if (!Number.isFinite(included) || included <= 0) {
    included = includedSeatsForTier(opts.tier);
  }
  let extra = Number(opts.extraSeats);
  if (!Number.isFinite(extra) || extra < 0) extra = 0;

  let maxSeats = Number(opts.maxSeats);
  if (!Number.isFinite(maxSeats) || maxSeats <= 0) {
    maxSeats = included + extra;
  }

  if (isInternalDemoSeatAllowance(opts)) {
    return Math.min(Math.max(Math.floor(maxSeats), 1), INTERNAL_DEMO_MAX_SEATS);
  }

  const tier = normalizeTier(opts.tier);
  if (opts.enterpriseUnlimited === true && (tier === 'enterprise' || tier === 'c')) {
    return Math.max(Math.floor(maxSeats), 1);
  }

  return Math.min(Math.max(Math.floor(maxSeats), 1), CUSTOMER_MAX_SEATS);
}

export function resolveSeatEntitlement(opts: OrbisSeatOpts = {}): {
  includedSeats: number;
  maxSeats: number;
} {
  let included = includedSeatsForTier(opts.tier);
  const metaIncluded = Number(opts.includedSeats);
  if (
    Number.isFinite(metaIncluded) &&
    metaIncluded > 0 &&
    (isInternalDemoSeatAllowance(opts) || metaIncluded <= included)
  ) {
    included = Math.floor(metaIncluded);
  }
  let maxSeats = resolveMaxSeats({
    ...opts,
    includedSeats: included,
  });
  if (!isInternalDemoSeatAllowance(opts) && maxSeats < included) {
    maxSeats = Math.min(included, CUSTOMER_MAX_SEATS);
  }
  return { includedSeats: included, maxSeats };
}

/** UI-Meter: Deckel aus max_seats, Fallback included_seats. */
export function seatCeiling(
  includedSeats?: number | null,
  maxSeats?: number | null,
): number | null {
  if (typeof maxSeats === 'number' && maxSeats > 0) return maxSeats;
  if (typeof includedSeats === 'number' && includedSeats > 0) return includedSeats;
  return null;
}
